import React from 'react'
import { FormTemplate, Service, ServiceCategory, ValidationError, api } from '../../api/client'
import { CATEGORIES } from '../../catalog/format'
import Field, { errorId, useFocusFirstInvalid } from './Field'
import { useDialog } from '../../hooks/useDialog'
import { LengthRow, MAX_LENGTHS, ServiceForm, toServiceForm, toServiceInput } from './serviceForm'
import { saveErrorMessage } from './toast'
import '../../components/Sheet.css'
import './Settings.css'
import { Icon, IconButton } from '../../ui/Icon'

interface ServiceSheetProps {
  service: Service | null
  onClose: () => void
  onSaved: (message: string) => void
}

export default function ServiceSheet({ service, onClose, onSaved }: ServiceSheetProps) {
  const [form, setForm] = React.useState<ServiceForm>(() => toServiceForm(service))
  const [errors, setErrors] = React.useState<Record<string, string>>({})
  const [busy, setBusy] = React.useState(false)
  const dialogRef = React.useRef<HTMLDivElement>(null)
  useDialog(dialogRef, onClose, { closable: !busy })
  const [error, setError] = React.useState('')
  const [confirmDelete, setConfirmDelete] = React.useState(false)
  // Her categories. A new account has none (A2): the form then opens on
  // "Add a category" as its first step.
  const [categories, setCategories] = React.useState<ServiceCategory[]>(() =>
    service ? CATEGORIES.filter((c) => c.value === service.category).map((c) => ({ ...c, custom: false })) : [],
  )
  const [categoriesLoaded, setCategoriesLoaded] = React.useState(false)
  const formRef = React.useRef<HTMLFormElement>(null)
  useFocusFirstInvalid(errors, formRef)
  // Her consultation forms; archived ones can't be picked.
  const [forms, setForms] = React.useState<FormTemplate[]>([])
  React.useEffect(() => {
    api.listFormTemplates().then((t) => setForms(t.filter((f) => !f.archived_at))).catch(() => {})
  }, [])
  const [addingCategory, setAddingCategory] = React.useState(false)
  const [newCategory, setNewCategory] = React.useState('')
  const [categoryError, setCategoryError] = React.useState('')
  React.useEffect(() => {
    api
      .listServiceCategories()
      .then((list) => {
        setCategories(list)
        setCategoriesLoaded(true)
        if (list.length === 0) setAddingCategory(true)
      })
      .catch(() => setCategoriesLoaded(true))
  }, [])
  const addCategory = async () => {
    setCategoryError('')
    try {
      const created = await api.createServiceCategory(newCategory)
      setCategories((list) => [...list, created])
      setForm((f) => ({ ...f, category: created.value }))
      setAddingCategory(false)
      setNewCategory('')
    } catch (err) {
      setCategoryError(err instanceof ValidationError ? (err.fields.label ?? 'Check the name.') : saveErrorMessage(err))
    }
  }

  const set = <K extends keyof ServiceForm>(key: K, value: ServiceForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }))
  // Choosing a category answers "Choose a category." at once.
  const chooseCategory = (value: ServiceForm['category']) => {
    set('category', value)
    setErrors((current) => {
      if (!current.category) return current
      const { category: _answered, ...rest } = current
      return rest
    })
  }
  // Each length row keeps a stable React key while rows come and go.
  const nextKey = React.useRef(0)
  const [keys, setKeys] = React.useState<number[]>(() => form.lengths.map(() => nextKey.current++))
  const [focusRow, setFocusRow] = React.useState<number | null>(null)
  const focusRef = React.useRef<HTMLInputElement>(null)
  React.useEffect(() => {
    if (focusRow !== null) focusRef.current?.focus()
  }, [focusRow])
  const setLength = (index: number, patch: Partial<LengthRow>) =>
    setForm((f) => ({ ...f, lengths: f.lengths.map((row, i) => (i === index ? { ...row, ...patch } : row)) }))
  // Length errors are keyed by row number, so they go once rows move.
  const dropLengthErrors = () =>
    setErrors((current) => Object.fromEntries(Object.entries(current).filter(([key]) => !key.startsWith('lengths'))))
  const addLength = () => {
    setForm((f) => ({ ...f, lengths: [...f.lengths, { minutes: '', price: '' }] }))
    setKeys((k) => [...k, nextKey.current++])
    setFocusRow(form.lengths.length)
  }
  const removeLength = (index: number) => {
    setForm((f) => ({ ...f, lengths: f.lengths.filter((_, i) => i !== index) }))
    setKeys((k) => k.filter((_, i) => i !== index))
    setFocusRow(null)
    dropLengthErrors()
  }
  // With no categories yet, the error belongs to the box she adds one in.
  const firstCategoryInvalid = !!errors.category && categories.length === 0 && addingCategory

  const save = async (e: React.FormEvent) => {
    e.preventDefault()
    let current = form
    // A category typed but not added yet (she pressed Save, not Add) is
    // created and used, rather than answered with "Choose a category."
    if (!current.category && addingCategory && newCategory.trim() !== '') {
      setCategoryError('')
      try {
        const created = await api.createServiceCategory(newCategory)
        setCategories((list) => [...list, created])
        current = { ...current, category: created.value }
        setForm(current)
        setAddingCategory(false)
        setNewCategory('')
      } catch (err) {
        setCategoryError(err instanceof ValidationError ? (err.fields.label ?? 'Check the name.') : saveErrorMessage(err))
        return
      }
    }
    const result = toServiceInput(current)
    if ('errors' in result) {
      setErrors(result.errors)
      return
    }
    setBusy(true)
    setErrors({})
    setError('')
    try {
      if (service) {
        await api.updateService(service.id, result.input, service.version)
        onSaved(`${result.input.name} saved`)
      } else {
        await api.createService(result.input)
        onSaved(`${result.input.name} added`)
      }
    } catch (err) {
      if (err instanceof ValidationError) {
        setErrors(err.fields)
      } else {
        setError(saveErrorMessage(err))
      }
      setBusy(false)
    }
  }

  const remove = async () => {
    if (!service) {
      return
    }
    setBusy(true)
    setError('')
    try {
      await api.deleteService(service.id)
      onSaved(`${service.name} deleted`)
    } catch (err) {
      setError(saveErrorMessage(err))
      setBusy(false)
    }
  }

  return (
    <div className="sheet-overlay settings-sheet" onClick={() => { if (!busy) onClose() }}>
      <div ref={dialogRef}
        className="sheet-content"
        role="dialog"
        aria-modal="true"
        aria-label={service ? `Edit ${service.name}` : 'Add a treatment'}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet-handle"></div>
        <div className="sheet-header">
          <h2>{service ? 'Edit treatment' : 'New treatment'}</h2>
        </div>

        <form ref={formRef} className="settings-form sheet-form" onSubmit={save} noValidate>
          <Field id="service-name" label="Name" error={errors.name}>
            <input id="service-name" value={form.name} onChange={(e) => set('name', e.target.value)} />
          </Field>

          {/* "Choose a category." marks the field invalid and takes focus like
              any other field error (QA 2026-10-14): the chip group (Field
              sets aria-invalid and aria-describedby on it; tabIndex -1 lets
              focusFirstInvalid reach it), or, with no categories yet, the
              "Add a category" box, which is all there is to fill in. */}
          <Field id="service-category" label="Category" error={errors.category}>
            <div
              className="chip-group"
              id={firstCategoryInvalid ? 'service-category-chips' : 'service-category'}
              role="group"
              aria-label="Category"
              tabIndex={errors.category && !firstCategoryInvalid ? -1 : undefined}
            >
              {categories.map((category) => (
                <button
                  key={category.value}
                  type="button"
                  className="chip"
                  aria-pressed={form.category === category.value}
                  onClick={() => chooseCategory(category.value)}
                >
                  <span className="category-dot" style={{ background: category.colour }} aria-hidden="true" />
                  {category.label}
                </button>
              ))}
              {!addingCategory && (
                <button type="button" className="chip chip--add" onClick={() => setAddingCategory(true)}>
                  <Icon name="plus" size={16} />
                  New category
                </button>
              )}
            </div>
            {categoriesLoaded && categories.length === 0 && (
              <p className="field-hint">Add a category first, like Nails or Brows. Your booking page groups your treatments by it.</p>
            )}
            {addingCategory && (
              <div className="new-category">
                <input
                  aria-label={categories.length === 0 ? 'Add a category' : 'New category name'}
                  aria-invalid={firstCategoryInvalid || undefined}
                  aria-describedby={firstCategoryInvalid ? errorId('service-category') : undefined}
                  placeholder="e.g. Facials"
                  maxLength={40}
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (newCategory.trim() !== '') addCategory() } }}
                />
                <button type="button" className="btn btn-secondary btn-compact" onClick={addCategory} disabled={newCategory.trim() === ''}>
                  Add
                </button>
                {categories.length > 0 && (
                  <button type="button" className="btn btn-quiet btn-compact" onClick={() => { setAddingCategory(false); setCategoryError('') }}>
                    Cancel
                  </button>
                )}
              </div>
            )}
            {categoryError && <p className="field-error" role="alert">{categoryError}</p>}
          </Field>

          {/* Lengths: one row per way she sells it (60 min for £99, 90 min
              for £129). Rows stay in the order she typed; the API returns
              them shortest first. */}
          <fieldset className="length-list" aria-describedby={errors.lengths ? errorId('service-lengths') : undefined}>
            <legend>Lengths</legend>
            {form.lengths.map((row, i) => {
              const minutesId = `service-length-${i}-minutes`
              const priceId = `service-length-${i}-price`
              const minutes = row.minutes.trim()
              return (
                <div key={keys[i]} className="length-row">
                  <div className="field-pair">
                    <Field id={minutesId} label="Length (minutes)" error={errors[`lengths[${i}].duration_minutes`]}>
                      <input
                        id={minutesId}
                        ref={i === focusRow ? focusRef : undefined}
                        type="number"
                        inputMode="numeric"
                        min={5}
                        max={480}
                        step={5}
                        value={row.minutes}
                        onChange={(e) => setLength(i, { minutes: e.target.value })}
                      />
                    </Field>
                    <Field id={priceId} label="Price (£)" error={errors[`lengths[${i}].price_pence`]}>
                      <input
                        id={priceId}
                        inputMode="decimal"
                        value={row.price}
                        onChange={(e) => setLength(i, { price: e.target.value })}
                      />
                    </Field>
                  </div>
                  {form.lengths.length > 1 && (
                    <IconButton
                      icon="close"
                      size={18}
                      className="length-row__remove"
                      label={/^\d+$/.test(minutes) ? `Remove ${minutes}-minute length` : 'Remove this length'}
                      onClick={() => removeLength(i)}
                    />
                  )}
                </div>
              )
            })}
            {errors.lengths && <p id={errorId('service-lengths')} className="field-error" role="alert">{errors.lengths}</p>}
            {form.lengths.length < MAX_LENGTHS && (
              <button type="button" className="btn btn-quiet btn-compact length-list__add" onClick={addLength}>
                <Icon name="plus" size={16} />
                Add another length
              </button>
            )}
          </fieldset>

          <Field
            id="service-buffer"
            label="Blocked time after (minutes)"
            hint="Keeps the room free after this treatment. Nothing can be booked into it."
            error={errors.buffer_after_minutes}
          >
            <input
              id="service-buffer"
              type="number"
              inputMode="numeric"
              min={0}
              max={480}
              step={5}
              value={form.buffer_after_minutes}
              onChange={(e) => set('buffer_after_minutes', e.target.value)}
            />
          </Field>

          <Field id="service-description" label="Description (optional)" error={errors.description}>
            <textarea
              id="service-description"
              rows={3}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
            />
          </Field>

          <Field
            id="service-form"
            label="Consultation form"
            hint="Clients are asked to fill this in before their appointment."
            error={errors.form_template_id}
          >
            <select id="service-form" value={form.form_template_id} onChange={(e) => set('form_template_id', e.target.value)}>
              <option value="">None</option>
              {forms.map((f) => (
                <option key={f.id} value={f.id}>{f.name}</option>
              ))}
            </select>
          </Field>

          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={form.bookable_online}
              onChange={(e) => set('bookable_online', e.target.checked)}
            />
            Clients can book this online
          </label>

          {error && <p className="field-error" role="alert">{error}</p>}

          {service && confirmDelete ? (
            // Deleting asks once, in place of Save: the final confirm is the
            // only solid red, with the way back below it (docs/25).
            <div className="sheet-stack">
              <p className="field-hint">{service.name} comes off your menu and booking page. Appointments already booked keep it.</p>
              <button type="button" className="btn btn-danger-solid btn-block" onClick={remove} disabled={busy}>
                {busy ? 'Deleting…' : `Delete ${service.name}`}
              </button>
              <button type="button" className="btn btn-secondary btn-block" onClick={() => setConfirmDelete(false)} disabled={busy}>
                Keep treatment
              </button>
            </div>
          ) : (
            <>
              <div className="sheet-actions">
                <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={busy}>
                  {busy ? 'Saving…' : 'Save'}
                </button>
              </div>

              {service && (
                <button type="button" className="btn btn-danger btn-compact" onClick={() => setConfirmDelete(true)} disabled={busy}>
                  Delete treatment
                </button>
              )}
            </>
          )}
        </form>
      </div>
    </div>
  )
}
